import React, { useEffect } from 'react';
import AnimatedBackground from '../components/AnimatedBackground';
import { GYM_INFO, MEMBERSHIPS } from '../mock';
import { Check, Star } from 'lucide-react';

const Memberships = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <AnimatedBackground />

      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-black text-white mb-4 uppercase">
            Membership <span className="text-[#39FF14]">Plans</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Choose the perfect plan for your fitness goals. All plans include full gym access and world-class equipment.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
          {MEMBERSHIPS.map((plan, index) => (
            <div
              key={plan.id}
              className={`relative bg-gradient-to-br from-[#1a1a1a] to-black p-8 rounded-3xl transition-all duration-300 hover:scale-105 ${
                plan.badge
                  ? 'border-2 border-[#39FF14] shadow-2xl shadow-[#39FF14]/20'
                  : 'border border-[#39FF14]/20 hover:border-[#39FF14]'
              }`}
              style={{
                transform: plan.badge ? 'scale(1.05)' : 'scale(1)',
              }}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-[#39FF14] rounded-full shadow-lg">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-black fill-black" />
                    <span className="text-black font-bold text-sm uppercase">{plan.badge}</span>
                  </div>
                </div>
              )}

              {/* Plan Name */}
              <div className="text-center mb-8">
                <h3 className="text-3xl font-black text-white mb-2 uppercase">{plan.name}</h3>
                <p className="text-gray-400 text-sm">{plan.duration}</p>
              </div>

              {/* Price */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-gray-400 text-2xl">₹</span>
                  <span className="text-6xl font-black text-[#39FF14]">{plan.price}</span>
                </div>
                {index === 2 && (
                  <p className="text-gray-500 text-sm mt-2">
                    Save ₹{(2499 * 12 - plan.price).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-gray-300">
                    <div className="w-6 h-6 bg-[#39FF14]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#39FF14]" />
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <a
                href={`${GYM_INFO.whatsappLink}?text=Hi, I want to join Iron Crust Gym with ${plan.name} membership (₹${plan.price})`}
                target="_blank"
                rel="noopener noreferrer"
                className={`block w-full py-4 font-bold text-center uppercase rounded-full transition-all duration-300 ${
                  plan.badge
                    ? 'bg-[#39FF14] text-black hover:scale-105 hover:shadow-xl hover:shadow-[#39FF14]/50'
                    : 'bg-white/10 text-white border-2 border-[#39FF14] hover:bg-[#39FF14] hover:text-black'
                }`}
              >
                Join Now
              </a>
            </div>
          ))}
        </div>

        {/* Features Comparison */}
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-12 uppercase">
            What's <span className="text-[#39FF14]">Included</span>
          </h2>

          <div className="bg-gradient-to-br from-[#1a1a1a] to-black p-8 md:p-12 rounded-3xl border border-[#39FF14]/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'State-of-the-art equipment',
                'Cardio & strength machines',
                'Free weights area',
                'Personal lockers',
                'Clean changing rooms',
                'Shower facilities',
                'Air-conditioned space',
                'Professional trainers on floor',
                'Progress tracking',
                'Diet guidance available',
                'Supplement store access',
                'Flexible training hours',
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#39FF14]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-[#39FF14]" />
                  </div>
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-6">
            Still have questions? We're here to help!
          </p>
          <a
            href={`${GYM_INFO.whatsappLink}?text=Hi, I have questions about membership plans`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-transparent text-white font-bold text-lg uppercase rounded-full border-2 border-[#39FF14] hover:bg-[#39FF14]/10 transition-all duration-300"
          >
            Chat with Us
          </a>
        </div>
      </div>
    </div>
  );
};

export default Memberships;
